'use client';
import React from 'react';
import { useTranslation } from 'react-i18next';

import Email from '@icons/Email.svg';
import Phone from '@icons/Phone.svg';
import Whatsapp from '@icons/Whatsapp.svg';

import { ClockLoader } from '@components/ClockLoader';
import { InfoContainer } from '@components/Common';
import { InformationField } from '@components/InformationField';
import { AdressText, TextContainer } from '@components/Listing';
import { ClickableAddress } from '@components/Listing/components/ClickableAddress';
import { StyledImage } from '@components/Listing/components/styles';
import { Action } from '@components/Modal';
import {
  ContentWrapper,
  ImageContainer,
  PhotoContainer,
  ProfileContentWrapper,
} from '@components/Profile/styles';
import { SchoolNameText, TimeInfoItem } from '@components/ShiftInfoCards/styles';

import { createEmailHref, createPhoneHref, openWhatsApp } from '@utils/helpers/contactHelpers';

interface ProfileInfoCardProps {
  firstName?: string | null;
  secondName?: string | null;
  imageUrl?: string | null;
  imageLoading?: boolean;
  email?: string | null;
  phoneNumber?: string | null;
  address?: string | null;
  whatsappEnabled?: boolean;
  showContactInfo?: boolean;
  substituteName?: string | null;
  teacherId?: string | null;
  centered?: boolean;
}

/**
 * ProfileInfoCard component displays the profile information of a user.
 * It shows the user's name, profile picture, and contact information.
 */
export const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({
  firstName,
  secondName,
  imageUrl,
  imageLoading = false,
  email,
  phoneNumber,
  address,
  whatsappEnabled = false,
  showContactInfo = false,
  substituteName,
  centered = true,
}) => {
  const { t } = useTranslation('common');

  // Use shared contact helpers instead of duplicate validation logic
  const getEmailHref = () => createEmailHref(email);
  const getPhoneHref = () => createPhoneHref(phoneNumber);
  const handleWhatsappClick = () => openWhatsApp(phoneNumber);

  return (
    <ProfileContentWrapper>
      <ContentWrapper>
        <PhotoContainer style={centered ? { alignItems: 'center' } : undefined}>
          <ImageContainer>
            {imageLoading ? (
              <ClockLoader />
            ) : (
              imageUrl && <StyledImage src={imageUrl} alt="Teacher Photo" />
            )}
          </ImageContainer>
          <InfoContainer style={centered ? { display: 'flex', alignItems: 'center' } : undefined}>
            <Action>
              {firstName} {secondName}
            </Action>
          </InfoContainer>
        </PhotoContainer>
        {showContactInfo && (address || email || phoneNumber) && (
          <InfoContainer>
            <SchoolNameText>{t('contact')}</SchoolNameText>
            {address && <ClickableAddress name={`${firstName} ${secondName}`} address={address} />}
            {email && (
              <TextContainer as="a" href={getEmailHref()}>
                <Email />
                <AdressText>{email}</AdressText>
              </TextContainer>
            )}
            <TimeInfoItem>
              {phoneNumber && (
                <TextContainer as="a" href={getPhoneHref()}>
                  <Phone />
                  <AdressText>{phoneNumber}</AdressText>
                </TextContainer>
              )}
              {whatsappEnabled && phoneNumber && (
                <TextContainer onClick={handleWhatsappClick} style={{ cursor: 'pointer' }}>
                  <Whatsapp />
                  <AdressText>{t('whatsapp')}</AdressText>
                </TextContainer>
              )}
            </TimeInfoItem>
          </InfoContainer>
        )}
      </ContentWrapper>
      {substituteName && (
        <InformationField
          variant="secondSubstitute"
          text={t('schoolShifts.substituteName', {
            substituteName,
          })}
        />
      )}
    </ProfileContentWrapper>
  );
};
